<template>
  <Teleport to="body">
    <div v-if="visible" class="BoardsOverlay" @click="emit('close')" @touchend.prevent="emit('close')">
      <div class="sphere-sub">
        <div ref="drawerRef" class="sub-card" :class="{ 'is-mobile': isMobile }" @click.stop @touchend.stop>
          <div class="sub-scroll">
            <div v-if="tab === 'ongoing'" class="sub-tab-header" @click.stop="switchTab('completed')">
              {{ localize('profile.boards.tab.my_active') }}
            </div>
            <div v-else class="sub-tab-header" @click.stop="switchTab('ongoing')">
              {{ localize('profile.boards.tab.my_completed') }}
            </div>

            <div class="sub-content">
              <template v-if="tab === 'ongoing'">
                <template v-if="isLoading">
                  <div class="loading-state">
                    <div class="skeleton-circle" />
                    <div class="skeleton-circle" />
                    <div class="skeleton-circle" />
                  </div>
                </template>

                <template v-else-if="activeEntries.length">
                  <div class="spots-grid">
                    <div
                      v-for="entry in activeEntries"
                      :key="entry.board.id + '-' + entry.locationId"
                      class="spot-item"
                      @click="handleSpotClick(entry)"
                    >
                      <div class="spot-avatar active">
                        <img
                          v-if="entry.locationCoverBlobHash"
                          :src="getImageSrc(entry.locationCoverBlobHash)"
                          :alt="entry.locationName"
                          loading="lazy"
                        />
                        <GamIcon v-else :name="GamIconName.LOCATION" size="24px" />
                      </div>
                      <span class="spot-name">{{ entry.locationName }}</span>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div class="empty-state">
                    <span class="empty-text">{{ localize('profile.boards.empty.active') }}</span>
                    <div class="empty-state-center">
                      <span class="board-squares-empty" @click="handleBoardEmptyClick" />
                    </div>
                  </div>
                </template>
              </template>

              <template v-if="tab === 'completed'">
                <template v-if="isLoading">
                  <div class="loading-state">
                    <div class="skeleton-circle" />
                    <div class="skeleton-circle" />
                    <div class="skeleton-circle" />
                  </div>
                </template>

                <template v-else-if="completedEntries.length">
                  <div class="completed-grid">
                    <div
                      v-for="entry in completedEntries"
                      :key="entry.board.id"
                      class="completed-board-card"
                      :class="{ 'is-world': entry.board.type === ChessboardType.WORLD }"
                      @click="handleBoardClick(entry)"
                    >
                      <div v-if="entry.board.plantedByUserId === authUser?.id" class="board-indicator planted-corner">
                        <GamIcon :name="GamIconName.OWNER" size="12px" is-current />
                      </div>
                      <div class="board-card-icon" />
                      <div class="completed-spot-label">{{ entry.locationName }}</div>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div class="empty-state">
                    <span class="empty-text">{{ localize('profile.boards.empty.completed') }}</span>
                    <div class="empty-state-center">
                      <span class="board-squares-empty" @click="handleBoardEmptyClick" />
                    </div>
                  </div>
                </template>
              </template>
            </div>
          </div>

          <div class="sphere-close" @touchend.prevent="emit('close')" @click="emit('close')">
            <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import type { MyBoardEntry, MyBoardsDto } from '@/core/data/chessboard/chessboard.interface';
import { ChessboardType } from '@/core/data/chessboard/chessboard.interface';
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import { gambits, localize } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useMapStore } from '@/stores/map.store';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { useDrawerGesture } from '@/views/composables/hooks/useDrawerGesture';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  visible: boolean;
  initialTab?: 'ongoing' | 'completed';
}>();

const emit = defineEmits<{
  close: [];
  'tab-change': [tab: 'ongoing' | 'completed'];
}>();

const router = useRouter();
const authStore = useAuthStore();
const { user: authUser } = storeToRefs(authStore);
const mapStore = useMapStore();

const drawerRef = ref<HTMLElement | null>(null);
useDrawerGesture(drawerRef, () => emit('close'));

const tab = ref<'ongoing' | 'completed'>(props.initialTab ?? 'ongoing');

function switchTab(newTab: 'ongoing' | 'completed') {
  tab.value = newTab;
  emit('tab-change', newTab);
}
const isLoading = ref(false);
const myBoards = ref<MyBoardsDto | null>(null);

const activeEntries = computed(() => {
  const all = myBoards.value?.active ?? [];
  const seen = new Map<string, MyBoardEntry>();
  for (const entry of all) {
    if (!seen.has(entry.locationId)) {
      seen.set(entry.locationId, entry);
    }
  }
  return [...seen.values()];
});

const completedEntries = computed(() => {
  const all = myBoards.value?.completed ?? [];
  const seen = new Map<string, MyBoardEntry>();
  for (const entry of all) {
    if (!seen.has(entry.board.id)) {
      seen.set(entry.board.id, entry);
    }
  }
  return [...seen.values()];
});

function getImageSrc(hash: string): string {
  return gambits.imageService.getImageSrc(hash, 160);
}

async function fetchMyBoards() {
  isLoading.value = true;
  const result = await gambits.chessboardService.getMyBoards();
  if (result.data) {
    myBoards.value = result.data;
  }
  isLoading.value = false;
}

function handleSpotClick(entry: MyBoardEntry) {
  emit('close');
  if (entry.locationCoordinates) {
    mapStore.setCurrentLocation({ lat: entry.locationCoordinates.lat, lng: entry.locationCoordinates.lng, zoom: 15 });
    router.push({ name: RouterNameType.LOCATIONS });
  }
}

function handleBoardClick(entry: MyBoardEntry) {
  router.push({ path: `/board/${entry.board.id}`, query: { from: 'sphere-completed' } });
}

function handleBoardEmptyClick() {
  emit('close');
  router.push({ name: RouterNameType.LOCATIONS });
}

/* Immediate because returning from a player's profile rebuilds PlayerDetail,
   which reopens us from the route query — so we mount already visible and there
   is no false-to-true transition to catch. */
watch(
  () => props.visible,
  (v) => {
    if (v) {
      tab.value = props.initialTab ?? 'ongoing';
      if (!myBoards.value) {
        fetchMyBoards();
      }
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.BoardsOverlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.sphere-sub {
  position: relative;
  width: 380px;
  max-width: calc(100vw - 2rem);
  height: min(700px, calc(100vh - 4rem));
  display: flex;
  flex-direction: column;
}

.sub-card {
  position: relative;
  flex: 1;
  min-height: 0;
  border-radius: 1.5rem;
  border: 1px solid var(--color-white-10);
  background: var(--color-dark-700);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.is-mobile {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    height: 85vh;
    border-radius: 1.5rem 1.5rem 0 0;
    will-change: transform;

    .sub-scroll {
      padding-bottom: var(--safe-bottom-offset);
    }
  }
}

.sub-scroll {
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  padding: 0.5rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &::-webkit-scrollbar {
    display: none;
  }
}

.sub-tab-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-s);
  padding: var(--spacing-l) 0;
  position: relative;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-white-70);
  cursor: pointer;
}

.sub-content {
  flex: 1;
  min-height: 0;
  padding-bottom: var(--safe-bottom-offset);
}

.sphere-close {
  position: absolute;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: manipulation;
  transition: all 200ms linear;
  z-index: 1;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press($transform: translateX(-50%));
}

/* ── Ongoing spots grid ────────────────────── */

.spots-grid {
  display: grid;
  grid-template-columns: repeat(3, 88px);
  justify-content: space-evenly;
  row-gap: var(--spacing-l);
  padding: var(--spacing-md) 0 var(--spacing-l);
}

.spot-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-s);
  width: 88px;
  text-decoration: none;
  cursor: pointer;
  transition: opacity var(--transition);

  @include hover {
    opacity: 0.8;
  }
}

.spot-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-dark-500);
  border: 2px solid var(--color-white-30);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &.active {
    border-color: var(--color-white-30);
  }
}

.spot-name {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: var(--color-white-70);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  max-width: 100%;
  line-height: 1.3;
}

/* ── Completed grid ────────────────────────── */

.completed-grid {
  display: grid;
  grid-template-columns: repeat(2, 160px);
  justify-content: space-evenly;
  row-gap: var(--spacing-l);
  padding: var(--spacing-md) 0 var(--spacing-l);
}

.completed-board-card {
  position: relative;
  width: 160px;
  height: 160px;
  border-radius: 0.75rem;
  overflow: hidden;
  border: 1px solid var(--color-white-10);
  cursor: pointer;
  transition: all 150ms ease;

  &.is-world {
    border-radius: 50%;

    .completed-spot-label {
      display: none;
    }
  }

  @include hover {
    border-color: var(--color-white-25);
    transform: scale(1.03);
  }
}

.board-card-icon {
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-size: 50% 50%;
  background-position:
    0 0,
    100% 0,
    0 100%,
    100% 100%;
  background-image: linear-gradient(#f0d9b5, #f0d9b5), linear-gradient(#b58863, #b58863),
    linear-gradient(#b58863, #b58863), linear-gradient(#f0d9b5, #f0d9b5);
}

.board-indicator {
  position: absolute;
  z-index: 2;
}

.planted-corner {
  top: 6px;
  right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: var(--color-linear-gradient-10), var(--color-dark-70);
  border-radius: 50%;
  color: var(--color-white);

  .is-world & {
    top: 24px;
    right: 24px;
  }
}

.completed-spot-label {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  padding: 4px 6px;
  font-size: 10px;
  font-weight: 500;
  color: var(--color-white-70);
  background: var(--color-dark-500);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Empty / loading states ────────────────── */

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--color-white-20);
  font-size: 14px;
  height: 100%;
  min-height: 300px;
}

.empty-text {
  padding-top: var(--spacing-l);
  white-space: pre-line;
  text-align: center;
}

.empty-state-center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.board-squares-empty {
  display: block;
  width: 48px;
  height: 48px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background-repeat: no-repeat;
  background-size: 50% 50%;
  background-position:
    0 0,
    100% 0,
    0 100%,
    100% 100%;
  background-image: linear-gradient(#f0d9b5, #f0d9b5), linear-gradient(#b58863, #b58863),
    linear-gradient(#b58863, #b58863), linear-gradient(#f0d9b5, #f0d9b5);
}

.loading-state {
  display: flex;
  justify-content: center;
  gap: var(--spacing-l);
  padding: var(--spacing-xl) 0;
}

.skeleton-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--color-white-5);
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

@keyframes skeleton-pulse {
  0%,
  100% {
    opacity: 0.4;
  }

  50% {
    opacity: 0.8;
  }
}
</style>
